import React from "react";
import { FileText } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";

const CONTENT = {
  en: {
    title: "Terms of Service",
    updated: "Last updated: June 2026",
    sections: [
      ["Acceptance", "By creating an account or using Suppev you agree to these Terms. If you do not agree, do not use the service."],
      ["Educational purpose only", "Suppev provides educational summaries of scientific evidence about supplements. It is NOT medical advice and does NOT diagnose, treat, prevent or cure any disease. Always consult a qualified healthcare professional before starting, stopping or changing any supplement, especially if you are pregnant, nursing, take medication or have a medical condition."],
      ["Anti-doping notice", "Anti-doping (WADA) status shown in the app is informational and based on available research and the current WADA Prohibited List. Athletes are solely responsible for verifying any product with their anti-doping authority before use. Suppev is not liable for competition eligibility outcomes."],
      ["Accounts", "You are responsible for keeping your login credentials secure and for all activity under your account."],
      ["Premium & billing", "Premium is a recurring subscription (monthly $9.99 or yearly $59.99) billed through Stripe until you cancel. You can cancel anytime from the Premium page; access continues until the end of the paid period. Prices may change with notice."],
      ["Acceptable use", "Do not misuse the service, attempt to reverse engineer it, or upload unlawful content."],
      ["Limitation of liability", "The service is provided \"as is\". To the maximum extent permitted by law, Suppev is not liable for any damages arising from use of the service or reliance on its content."],
      ["Contact", "Questions about these Terms? Email legal@suppev.com."],
    ],
  },
  es: {
    title: "Términos de Servicio",
    updated: "Última actualización: junio de 2026",
    sections: [
      ["Aceptación", "Al crear una cuenta o usar Suppev aceptas estos Términos. Si no estás de acuerdo, no uses el servicio."],
      ["Solo con fines educativos", "Suppev ofrece resúmenes educativos de la evidencia científica sobre suplementos. NO es consejo médico y NO diagnostica, trata, previene ni cura ninguna enfermedad. Consulta siempre a un profesional de la salud cualificado antes de iniciar, detener o cambiar cualquier suplemento, especialmente si estás embarazada, en lactancia, tomas medicación o tienes una condición médica."],
      ["Aviso antidopaje", "El estado antidopaje (WADA) mostrado en la app es informativo y se basa en la investigación disponible y en la Lista de Prohibiciones vigente de la WADA. Los atletas son los únicos responsables de verificar cualquier producto con su autoridad antidopaje antes de usarlo. Suppev no se hace responsable de resultados de elegibilidad en competición."],
      ["Cuentas", "Eres responsable de mantener seguras tus credenciales y de toda la actividad realizada en tu cuenta."],
      ["Premium y facturación", "Premium es una suscripción recurrente (mensual $9.99 o anual $59.99) cobrada a través de Stripe hasta que la canceles. Puedes cancelar cuando quieras desde la página Premium; el acceso continúa hasta el final del periodo pagado. Los precios pueden cambiar con previo aviso."],
      ["Uso aceptable", "No hagas mal uso del servicio, no intentes aplicar ingeniería inversa ni subas contenido ilegal."],
      ["Limitación de responsabilidad", "El servicio se ofrece \"tal cual\". En la máxima medida permitida por la ley, Suppev no se responsabiliza de daños derivados del uso del servicio o de la confianza en su contenido."],
      ["Contacto", "¿Dudas sobre estos Términos? Escribe a legal@suppev.com."],
    ],
  },
};

export default function Terms() {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.en;
  return (
    <div className="mx-auto max-w-3xl" data-testid="terms-page">
      <div className="mb-8 flex items-center gap-3">
        <Logo size={52} />
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-gray-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{c.title}</h1>
          <p className="text-sm text-gray-400">{c.updated}</p>
        </div>
      </div>
      <div className="space-y-6 rounded-2xl border border-gray-200 bg-white p-8">
        {c.sections.map(([h, body], i) => (
          <section key={i}>
            <h2 className="mb-1.5 flex items-center gap-2 text-lg font-bold text-gray-800">
              {i === 0 && <FileText size={17} className="text-emerald-500" />} {h}
            </h2>
            <p className="text-sm leading-relaxed text-gray-600">{body}</p>
          </section>
        ))}
      </div>
    </div>
  );
}
